import { useMutation, useQuery } from '@apollo/client';
import {
  AppContext,
  useUserMSPCheckHook,
  getTranslatedText,
  BackgroundIcon,
  AccessControl,
  DeleteModal,
  useIsAuthorisedHook,
  EventEmitter,
  NewTask,
  ResetFilter,
  ALL_TABLES
} from '@nile/components';
import { TicketContext } from '@nile/mangifera/src/Contexts';
import { DELETE_TICKET_VIEW } from '@nile/mangifera/src/Graphql/Mutations/ticketview';
import {
  ButtonGroup,
  Popover,
  Button,
  Heading
} from '@superops/ui-design-system';
import { Tooltip } from 'react-clone-tippy';
import { EllipsisV } from '@styled-icons/fa-solid/EllipsisV';
import React, {
  useCallback,
  useContext,
  useEffect,
  useReducer,
  useState
} from 'react';
import { withRouter } from 'react-router-dom';
import envConfig from '@nile/home/src/Config/env';
import styled from 'styled-components';
import { cloneDeep } from 'lodash';
import { injectIntl, FormattedMessage, useIntl } from 'react-intl';
import AdvancedTaskFilter from '@nile/mangifera/src/Components/Task/AdvancedTaskFilter';
import { DEFAULT_TASK_CONDITION } from '@nile/components/src/Pebbles/AdvancedTaskFilter';
import { SVGIcon } from '@superops/super-icons';
import { DELETE_TASK_VIEW } from '@nile/graphql/src/Mutation/task';
import { EditTaskView } from '@nile/mangifera/src/Components/Task/TaskView';

const ListViewIconWrapper = styled.div`
  opacity: 0.6;
`;

const KanbanIconWrapper = styled.div`
  opacity: 0.6;
`;

const StyledToolbar = styled.div`
  height: 90px;
  background: #ffffff;
  border-bottom: 1px solid #e7e7e7;
  padding: 20px;
  .ticketing-modify {
    display: flex;
    align-items: center;
  }
  .navMenu .Polaris-Button {
    padding: 0 10px !important;
  }
  .navMenu {
    margin-right: 20px !important;
  }
`;

const ToolbarWrapper = styled.div`
  .tag {
    font-size: 12px !important;
    padding: 2px 6px !important;
    margin-left: 12px !important;
  }
  .Polaris-Heading {
    font-weight: 500 !important;
  }
`;

const Action = styled.div`
  padding: 8px;
  cursor: pointer;
  border-radius: 4px;
  white-space: nowrap;
  &:hover {
    background: rgba(0, 0, 0, 0.05);
  }
`;

const MoreActivator = styled.div`
  border-radius: 50%;
  cursor: pointer;
  background: #ececec;
  &:hover {
    background: #ececec;
  }
  &:focus {
    outline: none;
  }
`;

const MoreActionsWrapper = styled.div`
  padding: 8px;
  min-width: 150px;
`;

function MoreActions({ onDeleteTaskView, isVisible, toggleEditTaskViewModal }) {
  const intl = useIntl();
  const [isPopoverVisible, togglePopover] = useReducer((s) => !s, false);

  const popoverActivator = (
    <MoreActivator className="ml8 flex p10" onClick={togglePopover}>
      <SVGIcon name="Actions" size="md" />
    </MoreActivator>
  );

  if (!isVisible) return null;

  return (
    <Popover
      active={isPopoverVisible}
      activator={popoverActivator}
      preferredAlignment="left"
      onClose={togglePopover}
    >
      <MoreActionsWrapper title="More actions">
        <AccessControl
          roleToCheck="taskViews.edit"
          render={
            <Action
              className="flex center"
              onClick={() => {
                togglePopover();
                toggleEditTaskViewModal();
              }}
            >
              <div className="mr-10">
                <SVGIcon name="Edit" />
              </div>
              <FormattedMessage id="taskView.modifyView" />
            </Action>
          }
        />

        <AccessControl
          roleToCheck="taskViews.delete"
          render={
            <Action className="flex center">
              <DeleteModal
                modalActivator={
                  <div className="pointer flex center">
                    <span className="mr10">
                      <SVGIcon name="Delete" />
                    </span>
                    <FormattedMessage id="common.delete" />
                  </div>
                }
                description={getTranslatedText(
                  intl,
                  'taskView.delete.confirmation'
                )}
                handleDeleteConfirmation={() => {
                  togglePopover();
                  onDeleteTaskView();
                }}
                title={getTranslatedText(intl, 'taskView.deleteView')}
              />
            </Action>
          }
        />
      </MoreActionsWrapper>
    </Popover>
  );
}

function TaskInfoToolbar(props) {
  const { intl, context, activeView } = props;
  const isMSP = useUserMSPCheckHook();

  const { taskViewType, setTaskViewType } = context;

  const tableName = ALL_TABLES.tasks;

  const isEditAuthorised = useIsAuthorisedHook('taskViews.edit');
  const isDeleteAuthorised = useIsAuthorisedHook('taskViews.delete');

  const [isEditTaskViewVisible, toggleEditTaskViewModal] = useReducer(
    (s) => !s,
    false
  );

  const [deleteTaskView] = useMutation(DELETE_TASK_VIEW);

  return (
    <>
      {isEditTaskViewVisible && (
        <EditTaskView onClose={toggleEditTaskViewModal} id={activeView.id} />
      )}
      <ToolbarWrapper className="flex full-width">
        <Heading element="h1" weight="Medium">
          <div className="flex center">
            <div className="flex center">
              <div className="mr8 navMenu" onClick={props.toggleNavMenu}>
                {props.navMenu}
              </div>
              {activeView.name}
              {activeView.shared ? (
                <Tooltip
                  title={getTranslatedText(intl, 'common.shared.view')}
                  delay={200}
                  duration={250}
                  position="bottom"
                  inertia
                  trigger="mouseenter"
                  animation="shift"
                  animateFill={false}
                >
                  <div className="fx center mr8 ml-16">
                    <SVGIcon name="GlobeSharedView" size="md" color="#6e7175" />
                  </div>
                </Tooltip>
              ) : (
                ''
              )}
            </div>
          </div>
        </Heading>

        <MoreActions
          isVisible={
            activeView.customView && (isEditAuthorised || isDeleteAuthorised)
          }
          onDeleteTaskView={() => {
            deleteTaskView({
              variables: {
                id: activeView.id,
                input: {
                  module: 'TASK'
                }
              }
            }).then((res) => {
              if (res?.data?.deleteView) {
                EventEmitter.dispatch('deleteTaskView');
              }
            });
          }}
          toggleEditTaskViewModal={toggleEditTaskViewModal}
        />

        <div className="flex center jend mlauto g10">
          <ResetFilter
            tableName={tableName}
            viewId={activeView.id}
            className="ml8 mr8 reset"
          />
          <ButtonGroup>
            <div className="view-type">
              {isMSP && (
                <ButtonGroup segmented>
                  <Tooltip
                    title={getTranslatedText(intl, 'ticket.listView.tooltip')}
                    delay={200}
                    duration={250}
                    position="bottom"
                    inertia
                    trigger="mouseenter"
                    animation="shift"
                    animateFill={false}
                  >
                    <Button
                      outline={taskViewType === 'list'}
                      onClick={() => setTaskViewType('list')}
                    >
                      <div data-qa="ticketListView">
                        <ListViewIconWrapper>
                          <SVGIcon name="ListView" size="sm" />
                        </ListViewIconWrapper>
                      </div>
                    </Button>
                  </Tooltip>
                  <Tooltip
                    title={getTranslatedText(intl, 'ticket.kanbanView.tooltip')}
                    delay={200}
                    duration={250}
                    position="bottom"
                    inertia
                    trigger="mouseenter"
                    animation="shift"
                    animateFill={false}
                  >
                    <Button
                      outline={taskViewType === 'kanban'}
                      onClick={() => {
                        setTaskViewType('kanban');
                        props.closeNavMenu();
                      }}
                    >
                      <KanbanIconWrapper data-qa="ticketKanbanView">
                        <SVGIcon size="sm" name="KanbanView" />
                      </KanbanIconWrapper>
                    </Button>
                  </Tooltip>
                </ButtonGroup>
              )}
            </div>
          </ButtonGroup>
          <AdvancedTaskFilter viewId={activeView?.id} tableName={tableName} />
        </div>
      </ToolbarWrapper>
    </>
  );
}

function TaskToolbar(props) {
  const { intl, match, navMenu } = props;

  const context = useContext(TicketContext);
  const activeView = context.activeView
    ? context.activeView
    : { name: 'All Tickets', id: 'all' };
  return (
    <StyledToolbar className="fx actr">
      <TaskInfoToolbar
        intl={intl}
        toggleNavMenu={props.toggleNavMenu}
        closeNavMenu={props.closeNavMenu}
        navMenu={navMenu}
        params={match.params}
        activeView={activeView}
        context={context}
        location={props.location}
        entitiesMeta={props.entitiesMeta}
      />
    </StyledToolbar>
  );
}

export default injectIntl(withRouter(TaskToolbar));
